import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon,
  ArrowLeftIcon,
  PlayIcon,
  ShareIcon
} from '@heroicons/react/24/outline'
import { useBlogPosts, formatFirestoreDate, getYouTubeEmbedUrl } from '../../../hooks/useFirestore'
import { where } from 'firebase/firestore'

export default function BlogPost() {
  const { slug } = useParams()
  const { getDocuments } = useBlogPosts()
  const [post, setPost] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return
      
      try {
        setLoading(true)
        setError(null)
        
        const posts = await getDocuments([
          where('slug', '==', slug),
          where('status', '==', 'published')
        ])
        
        if (posts.length > 0) {
          setPost(posts[0])
        } else {
          setError('Post no encontrado')
        }
      } catch (err) {
        setError('Error al cargar el post')
        console.error('Error loading post:', err)
      } finally {
        setLoading(false)
      }
    }

    loadPost()
  }, [slug, getDocuments])

  if (loading) {
    return (
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-8">
              Post no encontrado
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {error || 'El post que buscas no existe o no está disponible.'}
            </p>
            <button 
              onClick={() => window.history.back()}
              className="inline-flex items-center space-x-2 text-primary hover:text-secondary transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Volver al blog</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <button 
            onClick={() => window.history.back()}
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-primary transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Volver al blog</span>
          </button>
        </motion.div>

        <article>
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            {/* Type indicator */}
            <div className="flex items-center space-x-3 mb-6">
              {post.type === 'video' && (
                <div className="post-type-indicator post-type-video">
                  <PlayIcon className="w-4 h-4" />
                  <span>Video</span>
                </div>
              )}
              {post.type === 'social_link' && (
                <div className="post-type-indicator post-type-social">
                  <ShareIcon className="w-4 h-4" />
                  <span>Link Social</span>
                </div>
              )}
              {post.type === 'article' && (
                <div className="post-type-indicator post-type-article">
                  <span>📄</span>
                  <span>Artículo</span>
                </div>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
              {post.title}
            </h1>

            {/* Meta information */}
            <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5" />
                <span>{formatFirestoreDate(post.publishedAt)}</span>
              </div>
              {post.readTime && (
                <div className="flex items-center space-x-2">
                  <ClockIcon className="w-5 h-5" />
                  <span>{post.readTime} min de lectura</span>
                </div>
              )}
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full border border-primary/30"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </motion.header>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {/* Featured image */}
            {post.imageUrl && (
              <div className="mb-8">
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="w-full h-64 md:h-96 object-cover rounded-xl shadow-lg"
                />
              </div>
            )}

            {/* Video embebido (si es un post de video) */}
            {post.type === 'video' && post.videoUrl && (
              <div className="mb-8">
                <div className="blog-video-embed" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={getYouTubeEmbedUrl(post.videoUrl)}
                    title={post.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Excerpt */}
            <div className="text-xl text-gray-300 mb-8 p-6 bg-white/5 rounded-xl border border-white/10">
              {post.excerpt}
            </div>

            {/* Content (para artículos) */}
            {post.type === 'article' && post.content && (
              <div className="prose prose-lg prose-invert max-w-none">
                <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {post.content}
                </div>
              </div>
            )}

            {/* Enlaces sociales (para posts de social_link) */}
            {post.type === 'social_link' && post.socialLinks && (
              <div className="mt-8 p-6 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <h3 className="text-xl font-semibold text-purple-400 mb-4 flex items-center space-x-2">
                  <ShareIcon className="w-6 h-6" />
                  <span>Enlaces relacionados</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {post.socialLinks.facebook && (
                    <a 
                      href={post.socialLinks.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-3 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                    >
                      <span className="text-2xl">📘</span>
                      <span>Ver en Facebook</span>
                    </a>
                  )}
                  {post.socialLinks.twitter && (
                    <a 
                      href={post.socialLinks.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-3 bg-sky-500/20 text-sky-400 rounded-lg hover:bg-sky-500/30 transition-colors"
                    >
                      <span className="text-2xl">𝕏</span>
                      <span>Ver en X (Twitter)</span>
                    </a>
                  )}
                  {post.socialLinks.tiktok && (
                    <a 
                      href={post.socialLinks.tiktok} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-3 bg-pink-500/20 text-pink-400 rounded-lg hover:bg-pink-500/30 transition-colors"
                    >
                      <span className="text-2xl">🎵</span>
                      <span>Ver en TikTok</span>
                    </a>
                  )}
                  {post.socialLinks.instagram && (
                    <a 
                      href={post.socialLinks.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 rounded-lg hover:from-purple-500/30 hover:to-pink-500/30 transition-colors"
                    >
                      <span className="text-2xl">📷</span>
                      <span>Ver en Instagram</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </article>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 pt-8 border-t border-white/10"
        >
          <button 
            onClick={() => window.history.back()}
            className="inline-flex items-center space-x-2 text-primary hover:text-secondary transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Volver al blog</span>
          </button>
        </motion.div>
      </div>
    </div>
  )
}
